import { useNavigate } from "react-router-dom";
import { Target, Briefcase, Award, CheckCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import PageHero from "@/components/PageHero";
import CTABanner from "@/components/CTABanner";

const differentiators = [
  {
    id: 1,
    icon: Award,
    title: "20+ Years of Experience",
    description: "Over two decades of expertise in development of CAD-based applications and automation solutions for major CAD software platforms."
  },
  {
    id: 2,
    icon: Briefcase,
    title: "Industry Expertise",
    description: "Deep understanding of engineering workflows across automotive, aerospace, manufacturing, and design industries. We deliver solutions that work."
  },
  {
    id: 3,
    icon: Target,
    title: "Custom Solutions",
    description: "Every project is tailored to your specific needs. We don't believe in one-size-fits-all—we build what your business requires."
  }
];

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      
      <PageHero
        title="About Codeyantra Technologies"
        subtitle="Your trusted partner for CAD/CAM software development, automation, and customization with over 20 years of industry experience."
      >
        <div className="mb-6">
          <Breadcrumb />
        </div>
      </PageHero>

      {/* Mission Section */}
      <section data-testid="mission-section" className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] rounded-full text-sm font-medium mb-4">
                Who We Are
              </span>
              <h2 
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] mb-6"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Expert CAD/CAM Software Development Services
              </h2>
              <p className="text-lg text-[#0F172A]/70 leading-relaxed mb-6">
                Codeyantra is a specialized software development company with <strong>20+ years of experience</strong> in CAD/CAM software development, automation, and customization. We work with major CAD/CAM platforms including AutoCAD, SolidWorks, NX, Inventor, and more.
              </p>
              <p className="text-lg text-[#0F172A]/70 leading-relaxed">
                Our team of experienced developers and engineers has delivered hundreds of custom CAD/CAM solutions for clients worldwide—from simple automation scripts to enterprise-level applications that transform design and manufacturing workflows.
              </p>
            </div>

            <div className="order-first lg:order-last">
              <img
                src="https://images.unsplash.com/photo-1516534775068-ba3e7458af70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxzdHVkZW50cyUyMGxlYXJuaW5nJTIwY29tcHV0ZXJ8ZW58MHx8fHwxNzc1MTM5MjM0fDA&ixlib=rb-4.1.0&q=85"
                alt="Team working"
                data-testid="mission-image"
                className="w-full h-auto rounded-3xl border border-slate-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section data-testid="differentiators-section" className="py-16 lg:py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Why Choose Us
            </h2>
            <p className="mt-4 text-lg text-[#0F172A]/60 max-w-2xl mx-auto">
              What sets Codeyantra apart from the rest
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {differentiators.map((item) => (
              <div
                key={item.id}
                data-testid={`differentiator-card-${item.id}`}
                className="bg-white border border-slate-200 rounded-2xl p-8 hover:border-[#06B6D4]/30 transition-colors"
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-[#2563EB]/10 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-[#2563EB]" />
                </div>

                <h3 
                  className="text-xl font-bold text-[#0F172A] mb-3"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {item.title}
                </h3>
                <p className="text-[#0F172A]/60 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] mb-8"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Our Expertise
            </h2>

            <div className="space-y-4 text-left">
              {[
                "CAD/CAM software development and customization for AutoCAD, SolidWorks, NX, Inventor",
                "Custom plugin and add-in development using C#, VB.NET, Python, C++",
                "CAD/CAM API integration and external application connectivity",
                "Batch processing and manufacturing workflow automation solutions",
                "IoT integration and web-based engineering applications",
                "Legacy CAD/CAM system migration and modernization"
              ].map((expertise, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-4 bg-[#F8FAFC] rounded-xl"
                >
                  <CheckCircle className="w-5 h-5 text-[#06B6D4] mt-0.5 flex-shrink-0" />
                  <span className="text-[#0F172A]/80">{expertise}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTABanner 
        title="Let's Discuss Your Project"
        subtitle="Have a CAD automation challenge? We'd love to hear about it and explore how we can help."
        buttonText="Get in Touch"
        buttonLink="/contact"
      />

      <Footer />
    </div>
  );
};

export default AboutPage;